import { useEffect, useId, useState } from "react";
import { Button, Icon, cx } from "@omnabase/ui";

/** The "Add a path" field of a website's "Leave out" list (the send-and-sections round, 2026-10-23): a combobox that suggests the paths the
 *  source knows as the person types (`suggest`, asked a moment after the last key), and takes free text. Picking a suggestion (a click, or the
 *  arrows and Enter) puts it into the field; Enter or Add puts the field's path on the list. Escape closes the suggestions only. */
export function PathField({ suggest, onAdd, disabled }: { suggest: (q: string) => Promise<string[]>; onAdd: (path: string) => Promise<boolean>; disabled?: boolean }) {
  const listId = useId();
  const [value, setValue] = useState(""); const [options, setOptions] = useState<string[]>([]);
  const [open, setOpen] = useState(false); const [active, setActive] = useState(-1);
  useEffect(() => {
    const q = value.trim();
    if (!q.startsWith("/")) { setOptions([]); return; }
    let live = true;
    const t = setTimeout(() => { void suggest(q).then((paths) => { if (live) setOptions(paths.filter((p) => p !== q)); }).catch(() => { if (live) setOptions([]); }); }, 150);
    return () => { live = false; clearTimeout(t); };
  }, [value, suggest]);
  const take = (path: string) => { setValue(path); setOpen(false); setActive(-1); };
  const add = async () => { const path = value.trim(); if (!path) return; if (await onAdd(path)) { setValue(""); setOptions([]); setOpen(false); } };
  const shown = open && options.length > 0;
  return (
    <div className="ob-leave-out__add">
      <span className="ob-lookup">
        <input className="ob-lookup__input" role="combobox" aria-label="Add a path" aria-expanded={shown} aria-controls={listId} aria-autocomplete="list" placeholder="/blog/ or /docs/legacy/" value={value} disabled={disabled}
          onChange={(e) => { setValue(e.target.value); setOpen(true); setActive(-1); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && shown) { e.preventDefault(); setActive((active + 1) % options.length); }
            else if (e.key === "ArrowUp" && shown) { e.preventDefault(); setActive((active - 1 + options.length) % options.length); }
            else if (e.key === "Enter") { e.preventDefault(); if (shown && options[active]) take(options[active]!); else void add(); }
            else if (e.key === "Escape" && shown) { e.stopPropagation(); setOpen(false); }
          }} />
        {shown && (
          <span className="ob-lookup__list" role="listbox" id={listId} aria-label="Paths on this site">
            {options.map((p, i) => (
              <button type="button" key={p} role="option" aria-selected={i === active} className={cx("ob-lookup__option", i === active && "ob-lookup__option--active")} onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); take(p); }}>
                <span className="ob-lookup__icon"><Icon name={p.endsWith("/") ? "folderOpen" : "article"} size="sm" /></span><span className="ob-lookup__text"><span className="ob-lookup__title">{p}</span></span>
              </button>
            ))}
          </span>
        )}
      </span>
      <Button size="sm" disabled={disabled} onClick={() => void add()}>Add</Button>
    </div>
  );
}
